class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.predictiveSearchResultsWrapper = this.querySelector('[data-predictive-search-results]');
    this.defaultContent = this.querySelector('[data-predictive-search-default]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.recentElement = this.querySelector('.predictive-search-recent');
    this.clearRecent = this.querySelector('[data-recent-clear]');
    this.resultsCount = this.querySelector('.results-count');

    if (!this.getQuery()) {
      this.populateRecent();
    }
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.clearRecent.addEventListener('click', this.onClearRecent.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onClearRecent(event) {
    event.preventDefault();
    localStorage.removeItem('recentSearchQueries');
    this.recentElement.classList.add('hidden');
  }

  populateRecent() {
    const recentSearchQueriesString = localStorage.getItem('recentSearchQueries');

    if (!recentSearchQueriesString) return;

    const recentSearchQueriesArray = recentSearchQueriesString.split(',');

    if (recentSearchQueriesArray.length > 0) {
      this.recentElement.classList.remove('hidden');
      const recentElementUl = this.recentElement.querySelector('ul');
      recentElementUl.innerHTML = '';
      recentSearchQueriesArray.reverse().forEach((recentSearchQuery) => {
        const liElement = document.createElement('li');
        const aElement = document.createElement('a');
        aElement.href = `${routes.search_url}?q=${recentSearchQuery}`;
        aElement.textContent = recentSearchQuery;
        liElement.append(aElement);
        recentElementUl.append(liElement);
      });
    }
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.defaultContent.classList.add('hidden');
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
    this.defaultContent.classList.remove('hidden');
    this.close();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  saveToRecent(searchTerm) {
    let recentQueriesString = localStorage.getItem('recentSearchQueries');

    if (recentQueriesString === null) {
      recentQueriesString = searchTerm;
    } else {
      const recentQueriesArray = recentQueriesString.split(',');
      if (recentQueriesArray.indexOf(searchTerm) > -1) {
        return;
      }
      if (recentQueriesArray.length > 2) {
        recentQueriesArray.shift();
      }
      recentQueriesArray.push(searchTerm);
      recentQueriesString = recentQueriesArray.join(',');
    }
    localStorage.setItem('recentSearchQueries', recentQueriesString);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    console.log('getSearchResults');

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsDom = new DOMParser().parseFromString(text, 'text/html');
        const searchWrapper = resultsDom.querySelector('#predictive-search-results');
        const resultsMarkup = resultsDom.querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        const resultsCountTemplate = this.dataset.srOnlySearchCount;
        const resultsCountNoneTemplate = this.dataset.srOnlySearchCountNone;
        const resultsSize = parseInt(searchWrapper.dataset.size, 10);

        const translatedText =
          resultsSize > 0 ? resultsCountTemplate.replace('{{ count }}', resultsSize) : resultsCountNoneTemplate;
        if (this.resultsCount) {
          this.resultsCount.textContent = translatedText;
        }

        if (parseInt(searchWrapper.dataset.size) > 1) {
          this.defaultContent.classList.add('hidden');
          this.renderSearchResults(resultsMarkup);
          this.saveToRecent(queryKey);
        } else {
          this.predictiveSearchResultsWrapper.innerHTML = '';
          if (this.searchTerm === '') {
            this.defaultContent.classList.remove('hidden');
          }
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResultsWrapper.innerHTML = resultsMarkup;
    this.open();
  }

  open() {
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    console.log('CLOSE PREDICTIVE SEARCH');
    this.closeResults(clearSearchTerm);
    this.predictiveSearchResultsWrapper.innerHTML = '';
    this.defaultContent.classList.remove('hidden');
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', false);
  }
}

customElements.define('predictive-search', PredictiveSearch);
